import { Divider, Typography } from '@mui/material';
import images from 'assets/images';
import React from 'react';
import { Container } from '@mui/system';
import Slider from 'react-slick';
import { IBaseSearchRequest, IColumn, IPageModelBase } from 'models/base-type';
import { ORDER_BY_ASC, PAGE_DETAIL } from 'shared/constants/pagination';
import { Digits } from 'shared/enums/digits';
import healthService from 'services/home/home-service';
import { IHealthNewsModel } from 'models/health-news';
import { toast } from 'react-toastify';
import messages from 'shared/constants/messages';
import { DateUTCToLocalDateString } from 'utils/dateFormat';
import ModalForm from 'shared/ModalForm';
import ReactHtmlParser from 'react-html-parser';

const settingsNews = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 3,
    autoplay: true,
    responsive: [
        {
            breakpoint: 1024,
            settings: {
                slidesToShow: 3,
            },
        },
        {
            breakpoint: 900,
            settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
            },
        },
        {
            breakpoint: 600,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
            },
        },
    ],
};

const News: React.FC = () => {
    const totalCountRef = React.useRef(Digits.Zero);
    const pageRef = React.useRef<IPageModelBase>({ ...PAGE_DETAIL, order: ORDER_BY_ASC });
    const newsRef = React.useRef<IHealthNewsModel>(null);

    const [healthNews, setHealthNews] = React.useState<Array<IHealthNewsModel>>([]);
    const [openNewsModal, setOpenNewsModal] = React.useState(false);
    const settingsNewsSetting = {
        ...settingsNews,
        slidesToShow:
            healthNews?.length > 3
                ? settingsNews?.slidesToShow
                : healthNews?.length === Digits.Zero
                ? 1
                : healthNews?.length,
    };

    const columns: Array<IColumn> = React.useMemo(
        () => [
            {
                name: 'Order',
                data: 'order',
            },
            {
                name: 'News Date',
                data: 'newsDate',
            },
            {
                name: 'Title',
                data: 'title',
            },
            {
                name: 'Description',
                data: 'description',
            },
        ],
        [],
    );
    React.useEffect(() => {
        (async function fetchHealthNews() {
            await getHealthNews();
        })();
    }, []);

    const getHealthNews = async () => {
        const pageDetail = pageRef.current;
        const orderByColumnIndex = columns.findIndex((column: IColumn) => column.data === pageDetail.orderBy);
        const searchBody: IBaseSearchRequest = {
            start: pageDetail.page * pageDetail.rowsPerPage,
            length: -1,
            columns: columns,
            order: [
                {
                    column: orderByColumnIndex > -1 ? orderByColumnIndex : 0,
                    dir: pageDetail.order === ORDER_BY_ASC ? 0 : 1,
                },
            ],
        };

        await healthService
            .getHealthNews(searchBody)
            .then((response) => {
                totalCountRef.current = response?.data?.data?.totalRecords;
                setHealthNews(response?.data?.data?.records);
            })
            .catch(() => toast.error(messages.SomethingWentWrong));
    };

    const handleOpenNewsModal = () => setOpenNewsModal(true);
    const handleCloseNewsModal = () => setOpenNewsModal(false);

    const handleReadMoreClick = (news: IHealthNewsModel) => {
        newsRef.current = news;
        handleOpenNewsModal();
    };

    return (
        <section className="news" id="news">
            <a href="" className="scroll-anchor"></a>
            <Container maxWidth="lg">
                <Typography variant="h3" className="section-title">
                    Mental Health News
                </Typography>
                <div className="slider-block dymond-slider">
                    <Slider {...settingsNewsSetting}>
                        {healthNews?.length > 0 ? (
                            healthNews?.map((news: IHealthNewsModel) => {
                                return (
                                    <div key={news?.newsId} className="card-outer">
                                        <div className="card">
                                            <span className="date">{DateUTCToLocalDateString(news?.newsDate)}</span>
                                            <Typography variant="body1">{news?.title}</Typography>
                                            <Divider />
                                            <div className="MuiTypography-body2 reset-css">
                                                {ReactHtmlParser(news?.description)}
                                            </div>
                                            <div className="link-block">
                                                <button onClick={() => handleReadMoreClick(news)}>
                                                    Read more <img src={images.linkArrowIcon} alt="Arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="card-outer">
                                <div className="card">
                                    <span>{messages.NoRecordsFound}</span>
                                </div>
                            </div>
                        )}
                    </Slider>
                </div>
            </Container>
            <ModalForm
                openModal={openNewsModal}
                title={newsRef.current?.title ? newsRef.current?.title : 'Health News'}
                handleModalClose={handleCloseNewsModal}
                bodyContent={
                    <div className="MuiTypography-body2 reset-css">
                        {ReactHtmlParser(newsRef?.current?.description)}
                    </div>
                }
            />
        </section>
    );
};

export default React.memo(News);
